import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { ExternalLink, Heart, X } from 'lucide-react';
import { useArticlesStore, useAuthStore, useLikedArticlesStore, useFeedsStore, useFoldersStore } from '../store/useStore';
import { articles as articlesApi, social as socialApi } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import { openExternalLink } from '../utils/openExternalLink';
import { decodeHtmlEntities } from '../utils/decodeHtml';
import { formatAge, stamp } from '../utils/age';
import toast from 'react-hot-toast';
import ArticleSkeleton from './Skeleton';
import SwipeRow from './SwipeRow';
import PagedRows, { PagedRowsHandle } from './PagedRows';

interface FreshShare {
  id: string;
  createdAt: string;
  user: { id: string; username: string; displayName: string | null };
  article: { id: string; title: string; feed: { title: string } };
}

const FRESH_SHARE_MS = 7 * 24 * 60 * 60 * 1000;

type Bucket = 'today' | 'recent' | 'fading' | null;

export default function ArticleList() {
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token)!;
  const me = useAuthStore((state) => state.user);
  const articles = useArticlesStore((state) => state.articles);
  const updateArticle = useArticlesStore((state) => state.updateArticle);
  const filter = useArticlesStore((state) => state.filter);
  const setFilter = useArticlesStore((state) => state.setFilter);
  const bucket = useArticlesStore((state) => state.bucket);
  const setBucket = useArticlesStore((state) => state.setBucket);
  const feeds = useFeedsStore((state) => state.feeds);
  const selectedFeedId = useFeedsStore((state) => state.selectedFeedId);
  const selectedFolderId = useFeedsStore((state) => state.selectedFolderId);
  const setSelectedFeedId = useFeedsStore((state) => state.setSelectedFeedId);
  const setSelectedFolderId = useFeedsStore((state) => state.setSelectedFolderId);
  const decrementFeedUnreadCount = useFeedsStore((state) => state.decrementUnreadCount);
  const folders = useFoldersStore((state) => state.folders);
  const decrementFolderUnreadCount = useFoldersStore((state) => state.decrementUnreadCount);
  const { addLikedArticle, removeLikedArticle, isArticleLiked } = useLikedArticlesStore();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [totalUnreadCount, setTotalUnreadCount] = useState<number | null>(null);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [animatingLikes, setAnimatingLikes] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [freshShares, setFreshShares] = useState<FreshShare[]>([]);
  const lastSelectedIndexRef = useRef(-1);
  const autoMarkTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const articleRefs = useRef<(HTMLElement | null)[]>([]);
  const pagerRef = useRef<PagedRowsHandle | null>(null);

  // Today / Recent / Fading read as a stack of pages; All / Archive / Liked stay a flat list.
  const paged = filter === 'unread' && bucket !== null;

  // Brief skeleton so the list doesn't flash empty
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timer);
  }, []);

  // "From friends" strip: only shares from other people, newer than 7 days.
  useEffect(() => {
    let cancelled = false;
    socialApi.getShares(token, { limit: 20 })
      .then((shares: FreshShare[]) => {
        if (cancelled) return;
        const cutoff = Date.now() - FRESH_SHARE_MS;
        const fresh = shares.filter((share) =>
          share.user?.id !== me?.id && new Date(share.createdAt).getTime() > cutoff
        );
        setFreshShares(fresh.slice(0, 3));
      })
      .catch(() => { /* strip is optional; a failure just hides it */ });
    return () => { cancelled = true; };
  }, [token, me?.id]);

  // True unread count for the current scope (feed, folder, or everything), from the API.
  useEffect(() => {
    let cancelled = false;
    articlesApi.getUnreadCount(token, selectedFeedId || undefined, selectedFolderId || undefined)
      .then(({ count }) => { if (!cancelled) setTotalUnreadCount(count); })
      .catch(() => { if (!cancelled) setTotalUnreadCount(null); });
    return () => { cancelled = true; };
  }, [token, selectedFeedId, selectedFolderId, articles]);

  async function handleMarkRead(articleId: string, isRead: boolean, feedId?: string) {
    try {
      if (!isRead) {
        await articlesApi.markRead(token, articleId);
        updateArticle(articleId, { isRead: true });
        setTotalUnreadCount((n) => (n === null ? n : Math.max(0, n - 1)));
        if (feedId) {
          decrementFeedUnreadCount(feedId);
          const parentFolder = folders.find(f =>
            f.subscriptions?.some(sub => sub.feed?.id === feedId)
          );
          if (parentFolder) {
            decrementFolderUnreadCount(parentFolder.id);
          }
        }
      }
    } catch (error) {
      console.error('Failed to mark article as read:', error);
    }
  }

  function bumpUnread(feedId: string | undefined, delta: number) {
    if (!feedId) return;
    // The stores only expose "decrement"; a negative count bumps the number back up.
    decrementFeedUnreadCount(feedId, -delta);
    const parentFolder = folders.find(f => f.subscriptions?.some(sub => sub.feed?.id === feedId));
    if (parentFolder) decrementFolderUnreadCount(parentFolder.id, -delta);
  }

  async function handleMarkUnread(articleId: string, feedId?: string) {
    try {
      await articlesApi.markUnread(token, articleId);
      updateArticle(articleId, { isRead: false });
      setTotalUnreadCount((n) => (n === null ? n : n + 1));
      bumpUnread(feedId, 1);
    } catch (error) {
      console.error('Failed to mark article as unread:', error);
      toast.error('Could not mark as unread');
    }
  }

  // Swipe left on a touch screen: mark read, with a short window to undo.
  function swipeMarkRead(article: { id: string; isRead: boolean; feed: { id: string } }) {
    if (article.isRead) return;
    handleMarkRead(article.id, article.isRead, article.feed.id);
    toast((t) => (
      <span className="flex items-center gap-3">
        <span>Marked read</span>
        <button
          onClick={() => { toast.dismiss(t.id); handleMarkUnread(article.id, article.feed.id); }}
          className="btn btn-ink min-h-0 py-1 px-2"
        >
          Undo
        </button>
      </span>
    ), { duration: 4000, id: `read-${article.id}` });
  }

  // Swipe right on a read row: back to unread.
  function swipeMarkUnread(article: { id: string; isRead: boolean; feed: { id: string } }) {
    if (!article.isRead) return;
    handleMarkUnread(article.id, article.feed.id);
  }

  // Paged list: you moved on from a page, so what was on it is read now.
  async function markPageRead(indices: number[]) {
    const left = indices
      .map((i) => articles[i])
      .filter((a): a is typeof articles[number] => Boolean(a) && !a.isRead);
    if (left.length === 0) return;
    const ids = left.map((a) => a.id);

    // Optimistic: grey the rows and drop the counts now, confirm with one request.
    left.forEach((a) => updateArticle(a.id, { isRead: true }));
    setTotalUnreadCount((n) => (n === null ? n : Math.max(0, n - left.length)));
    const perFeed: Record<string, number> = {};
    left.forEach((a) => { perFeed[a.feed.id] = (perFeed[a.feed.id] || 0) + 1; });
    Object.entries(perFeed).forEach(([feedId, count]) => bumpUnread(feedId, -count));

    try {
      await articlesApi.markReadBatch(token, ids);
    } catch (error) {
      console.error('Failed to mark page as read:', error);
      left.forEach((a) => updateArticle(a.id, { isRead: false }));
      setTotalUnreadCount((n) => (n === null ? n : n + left.length));
      Object.entries(perFeed).forEach(([feedId, count]) => bumpUnread(feedId, count));
      toast.error('Could not mark that page as read');
      return;
    }

    toast((t) => (
      <span className="flex items-center gap-3">
        <span>{left.length === 1 ? 'Marked 1 read' : `Marked ${left.length} read`}</span>
        <button
          onClick={() => { toast.dismiss(t.id); undoPageRead(left); }}
          className="btn btn-ink min-h-0 py-1 px-2"
        >
          Undo
        </button>
      </span>
    ), { duration: 5000, id: 'page-read' });
  }

  async function undoPageRead(left: Array<{ id: string; feed: { id: string } }>) {
    try {
      await articlesApi.markUnreadBatch(token, left.map((a) => a.id));
      left.forEach((a) => updateArticle(a.id, { isRead: false }));
      setTotalUnreadCount((n) => (n === null ? n : n + left.length));
      const perFeed: Record<string, number> = {};
      left.forEach((a) => { perFeed[a.feed.id] = (perFeed[a.feed.id] || 0) + 1; });
      Object.entries(perFeed).forEach(([feedId, count]) => bumpUnread(feedId, count));
    } catch (error) {
      console.error('Failed to undo:', error);
      toast.error('Could not undo');
    }
  }

  // Everything this view can show: the active bucket (when in one) inside the
  // selected feed or folder. All / Archive have no bucket, so the whole scope.
  const markAllScope = {
    feedId: selectedFeedId || undefined,
    folderId: selectedFeedId ? undefined : selectedFolderId || undefined,
    bucket: filter === 'unread' && bucket ? bucket : undefined,
  };

  async function handleMarkAllRead() {
    try {
      const result = await articlesApi.markAllRead(token, markAllScope);

      const unreadCountByFeed: Record<string, number> = {};
      articles.forEach(article => {
        if (!article.isRead) {
          const feedId = article.feed.id;
          unreadCountByFeed[feedId] = (unreadCountByFeed[feedId] || 0) + 1;
        }
      });

      articles.forEach(article => {
        if (!article.isRead) {
          updateArticle(article.id, { isRead: true });
        }
      });

      Object.entries(unreadCountByFeed).forEach(([feedId, count]) => {
        decrementFeedUnreadCount(feedId, count);
        const parentFolder = folders.find(f =>
          f.subscriptions?.some(sub => sub.feed?.id === feedId)
        );
        if (parentFolder) {
          decrementFolderUnreadCount(parentFolder.id, count);
        }
      });

      toast.success(`Marked ${result.markedCount} as read`);
    } catch {
      toast.error('Failed to mark articles as read');
    }
  }

  // Rail button or shift+A: confirm in a toast, then mark everything in view as read
  function confirmMarkAllRead() {
    const where = selectedFeedId
      ? feeds.find((f) => f.feed.id === selectedFeedId)?.feed.title || 'this feed'
      : selectedFolderId
        ? folders.find((f) => f.id === selectedFolderId)?.name || 'this folder'
        : 'every feed';
    const when = markAllScope.bucket
      ? { today: 'Today', recent: 'Recent', fading: 'Fading' }[markAllScope.bucket]
      : null;
    const scope = when ? `${when} in ${where}` : where;
    toast((t) => (
      <span className="flex items-center gap-3">
        <span>Mark all in {scope} as read?</span>
        <button
          onClick={() => { toast.dismiss(t.id); handleMarkAllRead(); }}
          className="btn btn-ink min-h-0 py-1 px-2"
        >
          Yes
        </button>
        <button onClick={() => toast.dismiss(t.id)} className="btn btn-quiet min-h-0 py-1 px-2">
          No
        </button>
      </span>
    ), { duration: 8000, id: 'mark-all-confirm' });
  }

  // Keyboard navigation
  useEffect(() => {
    function handleKeyPress(e: KeyboardEvent) {
      if (document.activeElement?.tagName === 'INPUT' ||
          document.activeElement?.tagName === 'TEXTAREA') {
        return;
      }

      if (e.shiftKey && e.key === 'A') {
        e.preventDefault();
        confirmMarkAllRead();
        return;
      }

      // Paged list: space / page keys turn the page
      if (paged && (e.key === ' ' || e.key === 'PageDown' || e.key === 'PageUp')) {
        e.preventDefault();
        if (e.key === 'PageUp' || (e.key === ' ' && e.shiftKey)) pagerRef.current?.prev();
        else pagerRef.current?.next();
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'j':
          e.preventDefault();
          setSelectedIndex(prev => {
            const newIndex = Math.min(prev + 1, articles.length - 1);
            if (newIndex < articles.length) {
              setSelectedArticleId(articles[newIndex].id);
            }
            return newIndex;
          });
          break;
        case 'k':
          e.preventDefault();
          setSelectedIndex(prev => {
            const newIndex = Math.max(prev - 1, 0);
            if (newIndex >= 0 && newIndex < articles.length) {
              setSelectedArticleId(articles[newIndex].id);
            }
            return newIndex;
          });
          break;
        case 'enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < articles.length) {
            const article = articles[selectedIndex];
            handleMarkRead(article.id, article.isRead, article.feed.id);
            navigate(`/article/${article.id}`);
          }
          break;
        case 'escape':
          e.preventDefault();
          setSelectedIndex(-1);
          setSelectedArticleId(null);
          break;
        case 'l':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < articles.length) {
            const article = articles[selectedIndex];
            handleToggleLike(article.id, isArticleLiked(article.id));
          }
          break;
        case 'm':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < articles.length) {
            const article = articles[selectedIndex];
            if (!article.isRead) {
              handleMarkRead(article.id, false, article.feed.id);
              toast.success('Marked as read');
            }
          }
          break;
      }
    }

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      if (autoMarkTimeoutRef.current) {
        clearTimeout(autoMarkTimeoutRef.current);
      }
    };
  }, [articles, selectedIndex, navigate, token, updateArticle, isArticleLiked, selectedFeedId, feeds, paged]);

  // Keep selection stable when the article set changes
  useEffect(() => {
    const prevLength = articleRefs.current.length;
    const newLength = articles.length;

    if (prevLength !== newLength && Math.abs(prevLength - newLength) > 1) {
      setSelectedIndex(-1);
      setSelectedArticleId(null);
      lastSelectedIndexRef.current = -1;
      if (autoMarkTimeoutRef.current) {
        clearTimeout(autoMarkTimeoutRef.current);
        autoMarkTimeoutRef.current = null;
      }
    } else if (selectedArticleId) {
      const newIndex = articles.findIndex(a => a.id === selectedArticleId);
      if (newIndex !== -1) {
        setSelectedIndex(newIndex);
      }
    }

    if (articleRefs.current.length !== articles.length) {
      articleRefs.current.length = articles.length;
    }
  }, [articles, selectedArticleId]);

  // Scroll the selected row into view (flat list; the paged list turns to the row's page itself)
  useEffect(() => {
    if (paged) return;
    if (selectedIndex >= 0 && selectedIndex < articleRefs.current.length) {
      const articleElement = articleRefs.current[selectedIndex];
      if (articleElement) {
        articleElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [selectedIndex, paged]);

  // Auto-mark the row you moved off as read after a short pause
  useEffect(() => {
    if (autoMarkTimeoutRef.current) {
      clearTimeout(autoMarkTimeoutRef.current);
    }

    if (lastSelectedIndexRef.current >= 0 &&
        lastSelectedIndexRef.current !== selectedIndex &&
        lastSelectedIndexRef.current < articles.length) {

      const previousArticle = articles[lastSelectedIndexRef.current];

      if (previousArticle && !previousArticle.isRead) {
        autoMarkTimeoutRef.current = setTimeout(() => {
          handleMarkRead(previousArticle.id, false, previousArticle.feed.id);
        }, 500);
      }
    }

    lastSelectedIndexRef.current = selectedIndex;
  }, [selectedIndex, articles]);

  async function handleToggleLike(articleId: string, isLiked: boolean) {
    try {
      if (!isLiked) {
        setAnimatingLikes(prev => new Set(prev).add(articleId));
        setTimeout(() => {
          setAnimatingLikes(prev => {
            const next = new Set(prev);
            next.delete(articleId);
            return next;
          });
        }, 400);
      }

      if (isLiked) {
        await articlesApi.unlike(token, articleId);
        removeLikedArticle(articleId);
      } else {
        await articlesApi.like(token, articleId);
        addLikedArticle(articleId);
        trackEvent('article_like');
      }
      updateArticle(articleId, { isLiked: !isLiked });
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Failed to update article');
    }
  }

  // Tab rail. Today / Recent / Fading are the primary way through the fresh
  // window; All and Archive sit at the far end.
  function selectBucket(next: Bucket) {
    setFilter('unread');
    setBucket(next);
  }

  const primaryTabs: Array<{ key: Bucket; label: string }> = [
    { key: 'today', label: 'Today' },
    { key: 'recent', label: 'Recent' },
    { key: 'fading', label: 'Fading' },
  ];
  const inFreshWindow = filter === 'unread' || filter === 'all';
  const isAll = filter === 'all';
  const isArchive = filter === 'archive';
  const isLiked = filter === 'liked';

  const contextFeed = selectedFeedId ? feeds.find((f) => f.feed.id === selectedFeedId) : null;
  const contextFolder = selectedFolderId ? folders.find((f) => f.id === selectedFolderId) : null;
  const contextLabel = contextFeed
    ? (contextFeed.title || contextFeed.feed.title)
    : contextFolder
      ? contextFolder.name
      : null;

  function clearContext() {
    setSelectedFeedId(null);
    setSelectedFolderId(null);
  }

  const emptyLine = isLiked
    ? 'Nothing liked yet. Press l on an article to keep it here.'
    : isArchive
      ? 'The archive is empty.'
      : bucket === 'today'
        ? 'Nothing new today. Try Recent.'
        : bucket === 'recent'
          ? 'Nothing from the last few days.'
          : bucket === 'fading'
            ? 'Nothing is fading out right now.'
            : 'No articles here.';

  // One list row, shared by the flat and paged lists
  function renderRow(article: typeof articles[number], index: number) {
    const selected = index === selectedIndex;
    const liked = isArticleLiked(article.id);
    return (
      <SwipeRow
        key={article.id}
        leftLabel={article.isRead ? undefined : 'Mark read'}
        rightLabel={article.isRead ? 'Mark unread' : undefined}
        onSwipeLeft={() => swipeMarkRead(article)}
        onSwipeRight={() => swipeMarkUnread(article)}
        onClick={() => {
          handleMarkRead(article.id, article.isRead, article.feed.id);
          navigate(`/article/${article.id}`);
        }}
      >
      <article
        ref={(el) => { articleRefs.current[index] = el; }}
        className={`grid grid-cols-[3.5rem_1fr] lg:grid-cols-[4.5rem_1fr] border-b border-edge-subtle cursor-pointer bg-canvas ${
          selected ? 'bg-surface-sunken border-l-2 border-l-accent' : 'hover:bg-surface-sunken border-l-2 border-l-transparent'
        }`}
        style={{ scrollMarginTop: '3rem' }}
      >
        {/* Stamp column */}
        <div className="flex items-start justify-between gap-1 pl-2 lg:pl-3 pr-2 py-3 border-r border-edge-subtle">
          <span className={`stamp ${article.isRead ? 'text-ink-faint' : ''}`}>{stamp(index)}</span>
          {!article.isRead && <span className="dot mt-[5px]" aria-label="unread" />}
        </div>

        {/* Row body: text column plus an optional thumbnail on the right */}
        <div className="min-w-0 pl-3 lg:pl-4 pr-3 lg:pr-4 py-2.5 flex items-start gap-3 lg:gap-4">
        <div className="min-w-0 flex-1 max-w-list">
          <div className="kicker">
            {article.feed.title}
            {article.publishedAt && <> · {formatAge(article.publishedAt)}</>}
            {liked && <> · <Heart className="inline w-3 h-3 -mt-0.5 text-accent-600" fill="currentColor" /></>}
          </div>
          <h2 className={`font-sans text-[18px] font-semibold leading-snug tracking-normal ${
            article.isRead ? 'text-ink-muted' : 'text-ink'
          } ${selected ? '' : 'line-clamp-2'}`}>
            {decodeHtmlEntities(article.title)}
          </h2>

          {article.excerpt && (
            <p className={`mt-1 text-[14px] leading-relaxed line-clamp-2 ${
              article.isRead ? 'text-ink-faint' : 'text-ink-secondary'
            }`}>
              {decodeHtmlEntities(article.excerpt)}
            </p>
          )}

          {selected && (
            <div className="mt-2">
              <div className="flex items-center gap-4 kicker">
                <a
                  href={article.url}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openExternalLink(article.url);
                  }}
                  className="inline-flex items-center gap-1 hover:text-ink"
                >
                  Source <ExternalLink className="w-3 h-3" />
                </a>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleLike(article.id, liked);
                  }}
                  className={`inline-flex items-center gap-1 hover:text-ink ${liked ? 'text-accent-600' : ''}`}
                  title={liked ? 'Unlike' : 'Like'}
                >
                  <Heart
                    className={`w-3 h-3 ${animatingLikes.has(article.id) ? 'animate-like-bounce' : ''}`}
                    fill={liked ? 'currentColor' : 'none'}
                  />
                  {liked ? 'Liked' : 'Like'}
                </button>
                {article._count.comments > 0 && (
                  <span>{article._count.comments} {article._count.comments === 1 ? 'comment' : 'comments'}</span>
                )}
                {article._count.shares > 0 && (
                  <span>{article._count.shares} {article._count.shares === 1 ? 'share' : 'shares'}</span>
                )}
                {article._count.likes > 0 && (
                  <span>{article._count.likes} {article._count.likes === 1 ? 'like' : 'likes'}</span>
                )}
              </div>
            </div>
          )}
        </div>
        {article.imageUrl && (
          <img
            src={article.imageUrl}
            alt=""
            loading="lazy"
            draggable={false}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            className={`shrink-0 w-16 h-16 lg:w-24 lg:h-[4.5rem] object-cover border border-edge-subtle pointer-events-none select-none mt-0.5 ${
              article.isRead ? 'opacity-50' : ''
            }`}
          />
        )}
        </div>
      </article>
      </SwipeRow>
    );
  }

  const bucketLabel = bucket ? { today: 'Today', recent: 'Recent', fading: 'Fading' }[bucket] : '';
  const nextBucket: Bucket = bucket === 'today' ? 'recent' : bucket === 'recent' ? 'fading' : null;
  const nextBucketLabel = nextBucket ? { recent: 'Recent', fading: 'Fading' }[nextBucket] : null;

  // The page after the last one: leaving the last real page lands here, which marks it read.
  function renderEndPage() {
    return (
      <div className="h-full flex flex-col justify-center pl-4 lg:pl-6 pr-4 max-w-list">
        <div className="kicker mb-1">End of {bucketLabel}</div>
        <p className="text-ink-secondary">
          That's everything in {bucketLabel}{contextLabel ? ` for ${contextLabel}` : ''}.
        </p>
        <div className="mt-4 flex items-center gap-2">
          {nextBucket && (
            <button onClick={() => selectBucket(nextBucket)} className="btn btn-ink min-h-0 py-1.5 px-3">
              {nextBucketLabel} →
            </button>
          )}
          <button onClick={() => pagerRef.current?.goTo(0)} className="btn btn-quiet min-h-0 py-1.5 px-3">
            Back to top
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={paged ? 'flex-1 min-h-0 flex flex-col bg-canvas page-transition' : 'min-h-full bg-canvas page-transition'}>
      {/* Rail */}
      <div className={paged ? 'shrink-0 bg-canvas' : 'sticky top-0 z-10 bg-canvas'}>
        {contextLabel && (
          <div className="flex items-center gap-2 pl-4 lg:pl-6 pr-2 h-8 border-b border-edge-subtle">
            <span className="kicker">{contextFeed ? 'Feed' : 'Folder'}</span>
            <span className="text-sm font-semibold text-ink truncate">{contextLabel}</span>
            <button onClick={clearContext} className="p-1 text-ink-muted hover:text-ink" title="Show all feeds">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <div className="rail">
          {primaryTabs.map((tab) => (
            <button
              key={tab.label}
              onClick={() => selectBucket(tab.key)}
              className={`rail-tab ${inFreshWindow && !isAll && bucket === tab.key ? 'is-active' : ''}`}
            >
              {tab.label}
            </button>
          ))}
          {isLiked && <span className="rail-tab is-active">Liked</span>}
          {inFreshWindow && !isAll && bucket && (
            <button
              onClick={confirmMarkAllRead}
              className="rail-tab is-quiet"
              title="Mark everything in this view as read (shift+A)"
            >
              <span className="hidden sm:inline">Mark all read</span>
              <span className="sm:hidden">Read all</span>
            </button>
          )}
          <span className="rail-spacer" />
          {totalUnreadCount !== null && (
            <span className="kicker self-center px-2 lg:px-4 whitespace-nowrap text-ink" title="Unread in this view's feeds">
              {totalUnreadCount.toLocaleString()}<span className="hidden sm:inline"> unread</span>
            </span>
          )}
          <button
            onClick={() => { setFilter('all'); setBucket(null); }}
            className={`rail-tab is-quiet border-l border-edge ${isAll ? 'is-active' : ''}`}
          >
            All
          </button>
          <button
            onClick={() => { setFilter('archive'); setBucket(null); }}
            className={`rail-tab is-quiet border-r-0 ${isArchive ? 'is-active' : ''}`}
          >
            Archive
          </button>
        </div>
      </div>

      {/* From friends: only when a share is newer than 7 days */}
      {freshShares.length > 0 && !isLiked && !isArchive && (
        <div className="border-b border-edge shrink-0">
          <div className="flex items-center justify-between pl-4 lg:pl-6 pr-4 h-8 border-b border-edge-subtle">
            <span className="kicker text-ink">From friends</span>
            <button onClick={() => navigate('/friends')} className="kicker hover:text-ink">
              All →
            </button>
          </div>
          {freshShares.map((share) => (
            <button
              key={share.id}
              onClick={() => navigate(`/article/${share.article.id}`)}
              className="w-full text-left pl-4 lg:pl-6 pr-4 py-2 border-b border-edge-subtle last:border-b-0 hover:bg-surface-sunken"
            >
              <div className="kicker">
                {share.user.displayName || share.user.username} · {formatAge(share.createdAt)} · {share.article.feed.title}
              </div>
              <div className="text-[15px] font-semibold text-ink leading-snug line-clamp-1 max-w-list">
                {decodeHtmlEntities(share.article.title)}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Rows */}
      {isLoading && articles.length === 0 ? (
        <ArticleSkeleton count={6} />
      ) : articles.length === 0 ? (
        <div className="pl-4 lg:pl-6 pr-4 py-10 max-w-list">
          <div className="kicker mb-1">Empty</div>
          <p className="text-ink-secondary">{emptyLine}</p>
        </div>
      ) : paged ? (
        <PagedRows
          items={articles}
          renderItem={renderRow}
          renderEnd={renderEndPage}
          selectedIndex={selectedIndex}
          onLeavePage={markPageRead}
          controller={pagerRef}
        />
      ) : (
        <div>{articles.map((article, index) => renderRow(article, index))}</div>
      )}
    </div>
  );
}
